import {
  applySnapshot,
  flow,
  getSnapshot,
  Instance,
  types,
} from 'mobx-state-tree';
import { Files } from 'http-requests';
import { formatPhoneByLength, generateUniqueID } from 'utils';
import { IWorkersSelectCurrentSelection } from '../../../types';

/** ЗДЕСЬ МОДЕЛИ, КОТОРЫЕ ПЕРЕИСПОЛЬЗУЮТСЯ В РАЗНЫХ СТОРАХ **/

export interface IRequestStateModel extends Instance<
  typeof RequestStateModel
> {}
export interface IUploadFile extends Instance<typeof FileModel> {}

/* Единая модель состояния запроса */
export const RequestStateModel = types
  .model({
    pending: types.optional(types.boolean, false),
    fulfilled: types.optional(types.boolean, false),
    error: types.optional(types.boolean, false),
    requestCount: types.optional(types.number, 0),
  })
  .views((self) => ({
    get readyToLoad() {
      return !self.pending && !self.fulfilled && self.requestCount === 0;
    },
    get statuses() {
      return getSnapshot(self);
    },
  }))
  .actions((self) => ({
    loadingStates() {
      applySnapshot(self, {
        fulfilled: false,
        error: false,
        pending: true,
        requestCount: self.requestCount + 1,
      });
    },
    successStates() {
      applySnapshot(self, {
        ...self,
        fulfilled: true,
        error: false,
      });
    },
    errorStates() {
      applySnapshot(self, {
        ...self,
        fulfilled: false,
        error: true,
      });
    },
    finallyStates() {
      self.requestCount = Math.max(0, self.requestCount - 1);

      if (!self.requestCount) {
        applySnapshot(self, {
          ...self,
          pending: false,
        });
      }
    },
    clearStates() {
      applySnapshot(self, {
        pending: false,
        fulfilled: false,
        error: false,
        requestCount: 0,
      });
    },
  }));

/* Модель загружаемого файла (uploader, счётчики) */
const FileModel = types
  .model('FileModel', {
    id: types.optional(types.maybeNull(types.string), null),
    thumbnail_id: types.optional(types.maybeNull(types.string), null),
    file: types.optional(types.string, ''),
    filename: types.optional(types.maybeNull(types.string), null),
    is_published: types.optional(types.boolean, false),
    name: types.optional(types.string, ''),
    size: types.optional(types.maybeNull(types.number), null),
    uuid: types.optional(types.maybeNull(types.string), null),
    fileSrc: types.optional(types.maybe(types.string), ''),
    isLocal: types.optional(types.boolean, false),
    isPending: types.optional(types.boolean, false),
  })
  .volatile(() => ({
    originalFile: null as null | File,
    thumbnailSrc: '',
  }))
  .actions((self) => ({
    setOriginalFile(originalFile: File) {
      self.originalFile = originalFile;
      self.name = originalFile.name;
      self.filename = originalFile.name;
    },
    setSrc(url: string, thumbnailUrl: string) {
      self.fileSrc = url;
      self.thumbnailSrc = thumbnailUrl;
    },
    setPending(status: boolean) {
      self.isPending = status;
    },
    upload: flow(function* (
      uploadCallback: (file: File) => Promise<Partial<IUploadFile>>
    ) {
      if (!self.originalFile) {
        return;
      }

      try {
        self.isPending = true;
        const uploadedFile = yield uploadCallback(self.originalFile);

        if (!!uploadedFile) {
          self.id = uploadedFile.file ?? '';
          self.file = uploadedFile.file ?? '';
          self.thumbnail_id = uploadedFile.thumbnail ?? '';
          self.uuid = uploadedFile.uuid ?? '';
          self.isLocal = false;
        }

        return uploadedFile;
      } catch (error) {
        console.error(error);

        throw new Error(`Ошибка загрузки файла ${self.originalFile.name}`);
      } finally {
        self.isPending = false;
      }
    }),
    downloadFile: flow(function* (downloadCallback?: () => Promise<Blob>) {
      try {
        let blob: Blob;

        if (self.file) {
          const response = downloadCallback
            ? yield downloadCallback()
            : yield Files.GetCommonFile(self.file);

          blob = yield response.blob();
        } else {
          blob = new Blob([self.originalFile || '']);
        }

        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', self.name);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error(error);

        throw new Error('Ошибка при загрузке файла');
      }
    }),
  }));

/**
 * DTO (Data Transfer Object)
 *
 * Объект предназначен для получения данных с бэкенда, их обработки и адаптации под нужды фронтенда.
 * DTO также позволяет преобразовать изменённые данные обратно в формат, ожидаемый сервером,
 * перед их отправкой на бэкенд. Такой подход упрощает работу с данными и позволяет
 * избежать избыточной информации при обмене между клиентом и сервером.
 */
export const FileDTO = types.snapshotProcessor(FileModel, {
  preProcessor(sn: IUploadFile | File) {
    return {
      ...sn,
      file: ('file' in sn && sn.file) || '',
      name: sn.name || '',
    };
  },
  postProcessor(sn: IUploadFile) {
    const { fileSrc, isLocal, isPending, ...rest } = sn;

    return rest;
  },
});

/** Единая модель для работы с общим компонентом WorkersSelect (пример использования в Журнале заявок в двух местах) **/
export const CurrentSelectedCoordinators = types
  .model({
    departments: types.optional(types.array(types.string), []),
    positions: types.optional(types.array(types.string), []),
    workers: types.optional(types.array(types.string), []),
  })
  .views((self) => ({
    //...
  }))
  .actions((self) => ({
    setData(data: IWorkersSelectCurrentSelection) {
      self.departments.replace(data.departments);
      self.positions.replace(data.positions);
      self.workers.replace(data.workers);
    },
    clearData() {
      self.departments.clear();
      self.positions.clear();
      self.workers.clear();
    },
  }));

export interface IPhone extends Instance<typeof PhoneModel> {}

export const PhoneModel = types
  .model('PhoneModel', {
    id: types.optional(types.maybeNull(types.string), null),
    _type: types.optional(types.string, ''),
    phone_type: types.optional(types.string, 'cell'),
    code: types.optional(types.string, ''),
    number: types.optional(types.string, ''),
    add: types.optional(types.maybeNull(types.string), null),
    str_number: types.optional(types.maybeNull(types.string), null),
    not_actual: types.optional(types.boolean, false),
    added_by_tenant: types.optional(types.boolean, false),
    telegram: types.optional(types.maybeNull(types.boolean), false),
    whatsapp: types.optional(types.maybeNull(types.boolean), false),
    viber: types.optional(types.maybeNull(types.boolean), false),
  })
  .views((self) => ({
    get formatPhoneNumber(): string {
      return formatPhoneByLength(self.code + self.number || '');
    },
  }));

export const PhoneDTOModel = types.snapshotProcessor(PhoneModel, {
  preProcessor(sn: Partial<IPhone>) {
    return {
      ...sn,
      id: sn.id ?? generateUniqueID(),
      code: sn.code ?? '',
      number: sn.number ?? '',
    };
  },
});

export const AddNewThirdPartyModal = types.model({
  show: types.optional(types.boolean, false),
  addOrganization: types.optional(types.boolean, false),
});
